import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowUpRight, LogOut } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, formatApiError, getAdminToken, setAdminToken } from "@/lib/api";
import Dashboard from "@/components/admin/Dashboard";
import LeadsTable from "@/components/admin/LeadsTable";
import SettingsForm from "@/components/admin/SettingsForm";
import ContentAdmin from "@/components/admin/ContentAdmin";

function ResetPasswordScreen({ token, onDone }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    if (password.length < 8) return toast.error("A senha precisa ter pelo menos 8 caracteres.");
    if (password !== confirm) return toast.error("As senhas não conferem.");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      toast.success(data.message || "Senha redefinida com sucesso!");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível redefinir. O link pode ter expirado — solicite um novo."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-5">
      <div className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 shadow-xl" data-testid="admin-reset-card">
        <img src="/favicon.svg" alt="" className="h-12 w-12 rounded-xl" />
        <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Definir nova senha</h1>
        <p className="mt-1 text-sm text-slate-500">Crie uma nova senha com pelo menos 8 caracteres.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="reset-pass" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Nova senha</label>
            <Input id="reset-pass" data-testid="reset-password-input" type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          </div>
          <div>
            <label htmlFor="reset-confirm" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Confirmar nova senha</label>
            <Input id="reset-confirm" data-testid="reset-confirm-input" type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          </div>
          <button type="submit" data-testid="reset-submit" disabled={busy} className="w-full rounded-full bg-brand-600 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-60">
            {busy ? "Salvando..." : "Salvar nova senha"}
          </button>
        </form>
      </div>
    </div>
  );
}

function LoginScreen({ onLogin }) {
  const [view, setView] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setAdminToken(data.access_token);
      onLogin(data.user);
      toast.success("Bem-vinda de volta!");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível entrar. Verifique e-mail e senha."));
    } finally {
      setBusy(false);
    }
  }

  async function forgot(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message || "Verifique seu e-mail.");
      setView("login");
    } catch (err) {
      toast.error(formatApiError(err, "Não foi possível agora. Tente de novo."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-100 px-5">
      <div className="w-full max-w-sm rounded-3xl border border-slate-200 bg-white p-8 shadow-xl" data-testid="admin-login-card">
        <img src="/favicon.svg" alt="" className="h-12 w-12 rounded-xl" />
        {view === "login" ? (
          <>
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Painel da Isabela</h1>
            <p className="mt-1 text-sm text-slate-500">Área restrita — entre para ver seus leads.</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label htmlFor="admin-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">E-mail</label>
                <Input
                  id="admin-email"
                  data-testid="admin-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  autoComplete="email"
                />
              </div>
              <div>
                <label htmlFor="admin-password" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Senha</label>
                <Input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Sua senha"
                  autoComplete="current-password"
                />
              </div>
              <button type="submit" data-testid="admin-login-submit" disabled={busy} className="w-full rounded-full bg-brand-600 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-60">
                {busy ? "Entrando..." : "Entrar"}
              </button>
              <button
                type="button"
                data-testid="admin-forgot-link"
                onClick={() => { setView("forgot"); setBusy(false); }}
                className="w-full text-center text-sm font-bold text-brand-600 underline-offset-2 hover:underline"
              >
                Esqueci minha senha
              </button>
            </form>
          </>
        ) : (
          <>
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Recuperar senha</h1>
            <p className="mt-1 text-sm text-slate-500">Informe seu e-mail e enviaremos um link para criar uma nova senha.</p>
            <form onSubmit={forgot} className="mt-6 space-y-4">
              <div>
                <label htmlFor="forgot-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">E-mail</label>
                <Input
                  id="forgot-email"
                  data-testid="forgot-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  autoComplete="email"
                />
              </div>
              <button type="submit" data-testid="forgot-submit" disabled={busy} className="w-full rounded-full bg-brand-600 px-6 py-3.5 text-sm font-bold text-white transition-colors hover:bg-brand-700 disabled:opacity-60">
                {busy ? "Enviando..." : "Enviar link de redefinição"}
              </button>
              <button type="button" data-testid="forgot-back-link" onClick={() => setView("login")} className="w-full text-center text-sm font-bold text-slate-500 hover:text-slate-700">
                Voltar ao login
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(!!getAdminToken());
  const [tab, setTab] = useState("dashboard");
  const [unseen, setUnseen] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const resetToken = searchParams.get("reset");
  const pendingLeadId = searchParams.get("lead");

  useEffect(() => {
    if (!getAdminToken()) { setChecking(false); return; }
    api
      .get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => setAdminToken(null))
      .finally(() => setChecking(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    api
      .get("/admin/dashboard", { params: { period: "30d" } })
      .then(({ data }) => setUnseen(data.unseen_new_leads || 0))
      .catch(() => {});
  }, [user, tab]);

  if (resetToken) {
    return <ResetPasswordScreen token={resetToken} onDone={() => setSearchParams({})} />;
  }

  if (checking) {
    return <div className="min-h-screen bg-slate-100" data-testid="admin-loading" />;
  }

  if (!user) {
    return <LoginScreen onLogin={setUser} />;
  }

  const TABS = [
    { id: "dashboard", label: "Dashboard" },
    { id: "leads", label: "Leads", badge: unseen },
    { id: "content", label: "Conteúdo" },
    { id: "settings", label: "Configurações" },
  ];

  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-panel">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3.5 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/favicon.svg" alt="" className="h-9 w-9 rounded-lg" />
            <div className="leading-tight">
              <p className="font-display text-base font-bold text-slate-900">Painel da Isabela</p>
              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-brand-600">{user.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="/"
              data-testid="admin-open-site-link"
              className="hidden items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-xs font-bold text-slate-600 transition-colors hover:border-brand-600/40 hover:text-brand-600 sm:inline-flex"
            >
              Abrir site <ArrowUpRight className="h-3.5 w-3.5" />
            </a>
            <button
              data-testid="admin-logout-button"
              onClick={async () => {
                try { await api.post("/auth/logout"); } catch (e) {}
                setAdminToken(null);
                setUser(null);
              }}
              className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-slate-700"
            >
              <LogOut className="h-3.5 w-3.5" /> Sair
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="no-scrollbar flex gap-2 overflow-x-auto" role="tablist" aria-label="Seções do painel">
          {TABS.map((t) => (
            <button
              key={t.id}
              role="tab"
              aria-selected={tab === t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex shrink-0 items-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold transition-all ${
                tab === t.id ? "bg-brand-600 text-white shadow-md" : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {t.label}
              {t.badge > 0 && (
                <span data-testid="unseen-badge" className="flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-extrabold text-white">
                  {t.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {tab === "dashboard" && <Dashboard />}
          {tab === "leads" && (
            <LeadsTable
              pendingLeadId={pendingLeadId}
              onPendingHandled={() => setSearchParams(searchParams.get("reset") ? { reset: searchParams.get("reset") } : {})}
            />
          )}
          {tab === "content" && <ContentAdmin />}
          {tab === "settings" && <SettingsForm />}
        </div>
      </main>
    </div>
  );
}
